import React, {
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { SearchInput } from 'components/SearchInput';
import SecondaryNav from '../SecondaryNav';
import CollapsibleWave from './CollapsibleWave';
import Calendar from '../Calendar/CalendarContainer';
import { convertToArray } from 'Investigate/utils';
import { PERIODS } from '../constants';
import {
	Content,
	ContentHeader,
	Left,
	Right,
	LongHeaderColumn,
	HeaderColumn,
	SearchWrapper,
	SubHeader,
	Wrapper,
	WavesList,
	NoDataMessage,
} from './Planner.styles';

const Planner = ({
	initiatives,
	updateInitiativeDuration,
	waves,
	creators,
	initiativeCreator,
	initiativeWave,
	initiativeType,
	taxonomy,
	setQueryStringStateAndPush,
	period,
}) => {
	const [searchInitiative, setSearchInitiative] = useState(null);
	const [expandedWaves, setExpandedWaves] = useState(
		waves.reduce((acc, wave) => {
			return {
				...acc,
				[wave.id]: true,
			};
		}, {})
	);

	const [calendarContainerRef, setCalendarContainerRef] = useState(null);
	const [rightContainerRef, setRightContainerRef] = useState(null);
	const [wavesContainerRef, setWavesContainerRef] = useState(null);

	const { currency } = useContext(QueryStringContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);

	const clearSearchInitiative = useCallback(() => {
		setSearchInitiative(null);
		document.removeEventListener('click', clearSearchInitiative);
	}, []);

	const toggleExpandWave = id => {
		setExpandedWaves({
			...expandedWaves,
			[id]: !expandedWaves[id],
		});
	};

	const getFilteredInitiatives = () => {
		let filteredInitiativesList = initiatives.filter(item => !!item.waveId);

		if (initiativeType && initiativeType.length > 0) {
			filteredInitiativesList = filteredInitiativesList.filter(item =>
				initiativeType.includes(item.type)
			);
		}
		if (initiativeCreator && initiativeCreator.length > 0) {
			filteredInitiativesList = filteredInitiativesList.filter(item =>
				initiativeCreator.includes(item.createdBy)
			);
		}

		if (initiativeWave && initiativeWave.length > 0) {
			filteredInitiativesList = filteredInitiativesList.filter(item =>
				initiativeWave.includes(item.waveId && item.waveId.toString())
			);
		}

		if (taxonomy && taxonomy.length > 0) {
			const spendTypeTaxonomy = taxonomy.map(filter =>
				filter.split('_').map(item => Number(item))
			);

			filteredInitiativesList = filteredInitiativesList.filter(item =>
				spendTypeTaxonomy.some(
					taxonomyItem =>
						item.spendType === taxonomyItem[0] &&
						item.taxonomyType === taxonomyItem[1]
				)
			);
		}

		return filteredInitiativesList;
	};

	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const filteredInitiatives = getFilteredInitiatives();
	const noFilteredInitiatives =
		!filteredInitiatives || filteredInitiatives.length === 0;
	const wavesInitiatives = waves
		.map(wave => ({
			wave,
			initiatives: filteredInitiatives.filter(
				initiative => initiative.waveId === wave.id
			),
		}))
		.filter(({ initiatives }) => initiatives && initiatives.length);

	useEffect(() => {
		if (!period) {
			setQueryStringStateAndPush({ period: PERIODS[0] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(
		() => {
			setExpandedWaves(
				waves.reduce((acc, wave) => {
					return {
						...acc,
						[wave.id]: true,
					};
				}, {})
			);
		},
		[waves]
	);

	useEffect(
		() => {
			if (wavesContainerRef) {
				wavesContainerRef.addEventListener('scroll', onScrollWaves);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[wavesContainerRef]
	);

	useEffect(
		() => {
			if (calendarContainerRef) {
				calendarContainerRef.addEventListener('scroll', onScrollCalendar);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[calendarContainerRef]
	);

	const onScrollWaves = e => {
		window.requestAnimationFrame(() => {
			if (calendarContainerRef) {
				calendarContainerRef.removeEventListener('scroll', onScrollCalendar);
				calendarContainerRef.scrollTop = e.target.scrollTop;
				window.requestAnimationFrame(() => {
					calendarContainerRef.addEventListener('scroll', onScrollCalendar);
				});
			}
		});
	};

	const onScrollCalendar = e => {
		window.requestAnimationFrame(() => {
			if (wavesContainerRef) {
				wavesContainerRef.removeEventListener('scroll', onScrollWaves);
				wavesContainerRef.scrollTop = e.target.scrollTop;
				window.requestAnimationFrame(() => {
					wavesContainerRef.addEventListener('scroll', onScrollWaves);
				});
			}
		});
	};

	let rightContainerWidth = 0;
	if (rightContainerRef && rightContainerRef.getBoundingClientRect) {
		rightContainerWidth = rightContainerRef.getBoundingClientRect().width;
	}

	return (
		<Wrapper>
			<SecondaryNav creators={creators} waves={waves} />
			<Content>
				{noFilteredInitiatives ? (
					<NoDataMessage
						onCreate={() => {}}
						onReset={() => {
							setQueryStringStateAndPush({
								initiativeType: null,
								initiativeWave: null,
								initiativeCreator: null,
								taxonomy: null,
							});
						}}
					/>
				) : (
					<Fragment>
						<Left>
							<ContentHeader>
								<SearchWrapper>
									<SearchInput
										options={filteredInitiatives}
										onChange={initiative => {
											if (initiative) {
												setSearchInitiative(initiative);
												document.addEventListener(
													'click',
													clearSearchInitiative
												);
											}
										}}
										placeholder="Search"
										placement="top-start"
										charCount={2}
										showIcon
										isDismissible
									/>
								</SearchWrapper>
							</ContentHeader>
							<SubHeader>
								<LongHeaderColumn>Group name</LongHeaderColumn>
								<HeaderColumn>Low</HeaderColumn>
								<HeaderColumn>High</HeaderColumn>
							</SubHeader>
							<WavesList innerRef={r => setWavesContainerRef(r)}>
								{wavesInitiatives.map(({ wave, initiatives }) => (
									<CollapsibleWave
										key={wave.id}
										currencySymbol={currencySymbol}
										initiatives={initiatives}
										searchInitiative={searchInitiative}
										wave={wave}
										setIsExpanded={() => toggleExpandWave(wave.id)}
										isExpanded={expandedWaves[wave.id]}
									/>
								))}
							</WavesList>
						</Left>
						<Right innerRef={r => setRightContainerRef(r)}>
							<Calendar
								calendarContainerRef={r => setCalendarContainerRef(r)}
								initiatives={filteredInitiatives}
								waveInitiatives={wavesInitiatives}
								updateInitiativeDuration={updateInitiativeDuration}
								expandedWaves={expandedWaves}
								searchInitiative={searchInitiative}
								rightContainerWidth={rightContainerWidth}
							/>
						</Right>
					</Fragment>
				)}
			</Content>
		</Wrapper>
	);
};

export default props => {
	const {
		initiativeType,
		initiativeWave,
		initiativeCreator,
		period,
		taxonomy,
		setQueryStringStateAndPush,
	} = useContext(QueryStringContext);
	return (
		<Planner
			{...props}
			taxonomy={convertToArray(taxonomy)}
			initiativeType={convertToArray(initiativeType)}
			initiativeWave={convertToArray(initiativeWave)}
			initiativeCreator={convertToArray(initiativeCreator)}
			period={period}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
		/>
	);
};
